import React, { useState, useEffect, useMemo } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { productionTimeAPI, ProductionTimeEntry, ProductionState } from '../services/api';
import { showCustomAlert } from './CustomAlert';

const ProductionTimeEntries: React.FC = () => {
  const [entries, setEntries] = useState<ProductionTimeEntry[]>([]);
  const [states, setStates] = useState<ProductionState[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ProductionTimeEntry | null>(null);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState<Date[]>(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return [start, end];
  });
  const [summary, setSummary] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    stateId: '',
    startDate: new Date(),
    startTime: '00:00',
    endDate: null as Date | null,
    endTime: '',
    notes: ''
  });

  useEffect(() => {
    loadStates();
  }, []);

  useEffect(() => {
    if (states.length > 0 && dateRange[0] && dateRange[1]) {
      loadEntries();
    }
  }, [dateRange, states.length]);

  const loadStates = async () => {
    try {
      const statesData = await productionTimeAPI.getStates(true);
      setStates(statesData);
      
      // Pronađi "Redovan rad" (primary mode) i postavi kao podrazumevanu vrednost
      const primaryState = statesData.find(s => s.isPrimaryMode);
      if (primaryState && !formData.stateId) {
        setFormData(prev => ({ ...prev, stateId: primaryState._id }));
      }
    } catch (err: any) {
      console.error('Error loading states:', err);
      showCustomAlert.error('Greška pri učitavanju stanja');
    }
  };

  const loadEntries = async () => {
    if (!dateRange[0] || !dateRange[1]) return;
    
    setLoading(true);
    try {
      const startDate = dateRange[0].toISOString().split('T')[0];
      const endDate = dateRange[1].toISOString().split('T')[0];
      
      const data = await productionTimeAPI.getEntries({
        periodStart: startDate,
        periodEnd: endDate
      });
      
      setEntries(data.entries);
      setSummary(data.summary);
    } catch (err: any) {
      console.error('Error loading entries:', err);
      showCustomAlert.error('Greška pri učitavanju zapisa');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingEntry(null);
    // Pronađi "Redovan rad" (primary mode) i postavi kao podrazumevanu vrednost
    const primaryState = states.find(s => s.isPrimaryMode);
    setFormData({
      stateId: primaryState?._id || (states.length > 0 ? states[0]._id : ''),
      startDate: new Date(),
      startTime: new Date().toTimeString().slice(0, 5),
      endDate: null,
      endTime: '',
      notes: ''
    });
    setError('');
    setModalVisible(true);
  };

  const handleEdit = (entry: ProductionTimeEntry) => {
    setEditingEntry(entry);
    const stateId = typeof entry.stateId === 'object' ? entry.stateId._id : entry.stateId;
    const startDate = new Date(entry.startTime);
    const endDate = entry.endTime ? new Date(entry.endTime) : null;
    
    setFormData({
      stateId,
      startDate,
      startTime: startDate.toTimeString().slice(0, 5),
      endDate,
      endTime: endDate ? endDate.toTimeString().slice(0, 5) : '',
      notes: entry.notes || ''
    });
    setError('');
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    confirmDialog({
      message: 'Da li ste sigurni da želite da obrišete ovaj zapis?',
      header: 'Potvrda brisanja',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await productionTimeAPI.deleteEntry(id);
          await loadEntries();
        } catch (err: any) {
          showCustomAlert.error('Greška pri brisanju zapisa');
        }
      }
    });
  };

  const handleSubmit = async () => {
    if (!formData.stateId) {
      setError('Stanje je obavezno');
      return;
    }
    if (!formData.startDate) {
      setError('Datum početka je obavezan');
      return;
    }

    try {
      setError('');
      
      const [startHours, startMinutes] = formData.startTime.split(':').map(Number);
      const startDateTime = new Date(formData.startDate);
      startDateTime.setHours(startHours, startMinutes, 0, 0);
      
      let endDateTime: Date | undefined;
      if (formData.endDate && formData.endTime) {
        const [endHours, endMinutes] = formData.endTime.split(':').map(Number);
        endDateTime = new Date(formData.endDate);
        endDateTime.setHours(endHours, endMinutes, 0, 0);
      }

      if (editingEntry) {
        await productionTimeAPI.updateEntry(editingEntry._id, {
          stateId: formData.stateId,
          startTime: startDateTime.toISOString(),
          endTime: endDateTime?.toISOString(),
          notes: formData.notes
        });
      } else {
        await productionTimeAPI.createEntry({
          stateId: formData.stateId,
          startTime: startDateTime.toISOString(),
          endTime: endDateTime?.toISOString(),
          notes: formData.notes
        });
      }
      
      setModalVisible(false);
      await loadEntries();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Greška pri čuvanju zapisa');
    }
  };

  const stateOptions = useMemo(() => {
    return states.map(s => ({
      label: s.name,
      value: s._id
    }));
  }, [states]);

  const statusBodyTemplate = (rowData: ProductionTimeEntry) => {
    const state = typeof rowData.stateId === 'object' ? rowData.stateId : 
      states.find(s => s._id === rowData.stateId);
    
    if (!state) return '-';
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <i className={state.icon} style={{ color: state.color, fontSize: '1rem' }} />
        <span>{state.name}</span>
        {state.deductsFromPrimary && (
          <Tag value="Oduzimanje" severity="warning" style={{ fontSize: '0.75rem' }} />
        )}
      </div>
    );
  };

  const dateBodyTemplate = (rowData: ProductionTimeEntry, field: 'startTime' | 'endTime') => {
    const date = new Date(rowData[field] || '');
    if (!rowData[field]) return '-';
    return date.toLocaleString('sr-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const durationBodyTemplate = (rowData: ProductionTimeEntry) => {
    if (rowData.effectiveDuration !== undefined) {
      const hours = Math.floor(rowData.effectiveDuration / 60);
      const minutes = rowData.effectiveDuration % 60;
      return `${hours}h ${minutes}min`;
    }
    if (rowData.duration) {
      const hours = Math.floor(rowData.duration / 60);
      const minutes = rowData.duration % 60;
      return `${hours}h ${minutes}min`;
    }
    return '-';
  };

  const actionsBodyTemplate = (rowData: ProductionTimeEntry) => {
    return (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          icon="pi pi-pencil"
          className="p-button-text"
          onClick={() => handleEdit(rowData)}
          tooltip="Izmeni"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-danger"
          onClick={() => handleDelete(rowData._id)}
          tooltip="Obriši"
        />
      </div>
    );
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  return (
    <div style={{ padding: '2rem' }}>
      <ConfirmDialog />
      
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ margin: 0, marginBottom: '1rem' }}>Zapisi Vremena Proizvodnje</h1>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontWeight: 'bold' }}>Period:</label>
            <Calendar
              value={dateRange}
              onChange={(e) => setDateRange(e.value as Date[])}
              selectionMode="range"
              dateFormat="dd.mm.yy"
              showIcon
              readOnlyInput
            />
          </div>
          
          <Button
            label="Dodaj zapis"
            icon="pi pi-plus"
            className="p-button-success"
            onClick={handleAdd}
          />
          
          <Button
            label="Export PDF"
            icon="pi pi-file-pdf"
            onClick={() => {
              // TODO: Implement PDF export
              showCustomAlert.info('PDF export će biti implementiran uskoro');
            }}
          />
        </div>
      </div>

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
          <Card title="Redovan rad">
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {formatDuration(summary.regularMinutes || 0)}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              Vremenski opseg obuhvata {formatDuration(summary.totalMinutes || 0)}
            </div>
            {summary.totalDeductedMinutes > 0 && (
              <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                Oduzeto: {formatDuration(summary.totalDeductedMinutes)}
              </div>
            )}
          </Card>
          
          <Card title="Specijalni modovi">
            {summary.deductionByState && summary.deductionByState.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {summary.deductionByState.map((mode: any) => (
                  <div key={mode.stateId} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{mode.stateName} ({mode.entryCount} zapisa)</span>
                    <span style={{ fontWeight: 'bold' }}>{formatDuration(mode.totalMinutes)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666' }}>Nema zapisa</div>
            )}
          </Card>
        </div>
      )}

      <Card>
        <DataTable
          value={entries}
          loading={loading}
          paginator
          rows={20}
          emptyMessage="Nema zapisa"
        >
          <Column field="stateId" header="Stanje" body={statusBodyTemplate} sortable />
          <Column 
            field="startTime" 
            header="Početak" 
            body={(row) => dateBodyTemplate(row, 'startTime')} 
            sortable 
          />
          <Column 
            field="endTime" 
            header="Kraj" 
            body={(row) => dateBodyTemplate(row, 'endTime')} 
            sortable 
          />
          <Column field="duration" header="Trajanje" body={durationBodyTemplate} sortable />
          <Column field="notes" header="Napomene" />
          <Column body={actionsBodyTemplate} header="Akcije" style={{ width: '150px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editingEntry ? 'Izmeni zapis' : 'Novi zapis'}
        visible={modalVisible}
        style={{ width: '600px' }}
        onHide={() => setModalVisible(false)}
        footer={
          <div>
            <Button label="Otkaži" icon="pi pi-times" onClick={() => setModalVisible(false)} className="p-button-text" />
            <Button label={editingEntry ? 'Sačuvaj' : 'Kreiraj'} icon="pi pi-check" onClick={handleSubmit} />
          </div>
        }
      >
        {error && <Message severity="error" text={error} style={{ marginBottom: '1rem' }} />}
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label htmlFor="stateId" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Stanje <span style={{ color: 'red' }}>*</span>
            </label>
            <Dropdown
              id="stateId"
              value={formData.stateId}
              options={stateOptions}
              onChange={(e) => setFormData({ ...formData, stateId: e.value })}
              appendTo="self"
              style={{ width: '100%' }}
              placeholder="Izaberite stanje"
            />
          </div>
          
          <div>
            <label htmlFor="startDate" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Početak <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <Calendar
                id="startDate"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.value as Date })}
                dateFormat="dd.mm.yy"
                showIcon
                style={{ width: '100%' }}
              />
              <InputText
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="endDate" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Kraj
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <Calendar
                id="endDate"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.value as Date | null })}
                dateFormat="dd.mm.yy"
                showIcon
                style={{ width: '100%' }}
              />
              <InputText
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                style={{ width: '100%' }}
                placeholder="hh:mm"
              />
            </div>
            <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
              Ostavite prazno ako je još uvek u toku
            </small>
          </div>
          
          <div>
            <label htmlFor="notes" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Napomene
            </label>
            <InputTextarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              style={{ width: '100%' }}
              rows={3}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProductionTimeEntries;

